import {
  doc,
  setDoc as setDocOriginal,
  updateDoc as updateDocOriginal,
  deleteDoc as deleteDocOriginal,
  writeBatch,
  DocumentReference,
  UpdateData,
  DocumentData,
  QuerySnapshot,
  collection,
  orderBy,
  getDocs,
  query,
  limit,
  startAfter,
  getCountFromServer,
  startAt,
  QueryFieldFilterConstraint,
  where,
  WhereFilterOp,
  Query,
} from 'firebase/firestore';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useCollection as useCollectionSnaps,
  useCollectionOnce as useCollectionSnapsOnce,
  useDocumentData,
  useDocumentDataOnce,
} from 'react-firebase-hooks/firestore';
import { db } from '../firebase';
import { isNotNull } from './isNotNull';

export const getRef = <T>(path: string) => {
  const args = path.split('/');
  const firstArg = args[0] as string;
  const ref = doc(db, firstArg, ...args.slice(1)) as DocumentReference<T>;
  return ref;
};

export const setDoc = <T>(path: string, data: T) => setDocOriginal(getRef<T>(path), data);

export const updateDoc = <T>(path: string, data: UpdateData<T>) =>
  updateDocOriginal(getRef<T>(path), data as DocumentData);

export const deleteDoc = (path: string) => deleteDocOriginal(getRef(path));

export const createBatch = () => {
  const batch = writeBatch(db);
  return {
    set: <T>(path: string, data: T) => {
      batch.set(getRef<T>(path), data);
    },
    update: <T>(path: string, data: UpdateData<T>) => {
      batch.update(getRef<T>(path), data as DocumentData);
    },
    delete: (path: string) => {
      batch.delete(getRef(path));
    },
    commit: () => batch.commit(),
  };
};

type UsePaginationProps<T> = {
  path: string;
  orderByField: keyof T;
  orderDirection?: 'desc' | 'asc';
  perPage?: number;
  where?: QueryFieldFilterConstraint[];
};

const initWhere: QueryFieldFilterConstraint[] = [];

export function usePagination<T>({
  path,
  perPage = 10,
  orderByField,
  orderDirection = 'desc',
  where = initWhere,
}: UsePaginationProps<T>) {
  const isInitializatedRef = useRef(false);
  const [docs, setDocs] = useState<DocumentData[]>([]);
  const [data, setData] = useState<QuerySnapshot<DocumentData>>();
  const [prevDocRefs, setPrevDocRefs] = useState<DocumentData[]>([]);
  const [count, setCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const myCollection = useMemo(() => {
    const args = path.split('/');
    const firstArg = args[0] as string;
    return collection(db, firstArg, ...args.slice(1));
  }, [path]);

  const myOrderBy = useMemo(() => orderBy(orderByField as string, orderDirection), [orderByField, orderDirection]);

  const fetchDocs = useCallback((documentSnapshots: QuerySnapshot<DocumentData>) => {
    setData(documentSnapshots);
    const newDocs: DocumentData[] = [];
    documentSnapshots.forEach((doc) => newDocs.push({ ...doc.data(), id: doc.id }));
    setDocs(newDocs);
  }, []);

  const init = useCallback(async () => {
    setIsLoading(true);
    const documentSnapshots = await getDocs(query(myCollection, ...where, myOrderBy, limit(perPage)));
    fetchDocs(documentSnapshots);
    setIsLoading(false);
  }, [fetchDocs, myCollection, myOrderBy, perPage, where]);

  const getNext = useCallback(async () => {
    if (data?.docs) {
      setIsLoading(true);
      const prevDocRef = data?.docs[0];
      if (prevDocRef) {
        setPrevDocRefs((prev) => [...prev, prevDocRef]);
      }
      const documentSnapshots = await getDocs(
        query(myCollection, ...where, myOrderBy, limit(perPage), startAfter(data.docs[data.docs.length - 1]))
      );
      fetchDocs(documentSnapshots);
      setIsLoading(false);
    }
  }, [data?.docs, myCollection, myOrderBy, perPage, where, fetchDocs]);

  const getPrevious = useCallback(async () => {
    if (prevDocRefs.length) {
      setIsLoading(true);
      const documentSnapshots = await getDocs(
        query(myCollection, ...where, myOrderBy, limit(perPage), startAt(prevDocRefs[prevDocRefs.length - 1]))
      );
      setPrevDocRefs((prev) => prev.slice(0, prev.length - 1));
      fetchDocs(documentSnapshots);
      setIsLoading(false);
    }
  }, [fetchDocs, myCollection, myOrderBy, perPage, prevDocRefs, where]);

  const revalidate = useCallback(async () => {
    init();
    setPrevDocRefs([]);
  }, [init]);

  const onPageChange = useCallback(
    (newPage: number) => {
      if (newPage > prevDocRefs.length) {
        getNext();
      }
      if (newPage < prevDocRefs.length) {
        getPrevious();
      }
    },
    [getNext, getPrevious, prevDocRefs.length]
  );

  const getCount = useCallback(async () => {
    const snapshot = await getCountFromServer(query(myCollection, ...where));
    setCount(snapshot.data().count);
  }, [myCollection, where]);

  useEffect(() => {
    setPrevDocRefs([]);
    getCount();
    init();
  }, [init, getCount, where]);

  return {
    data: docs as DbDocument<T>[],
    getNext,
    getPrevious,
    onPageChange,
    revalidate,
    page: prevDocRefs.length,
    count: count || 0,
    isLoading,
  };
}

export function useDocument<T extends {}>(path: string | null) {
  const ref = useMemo(() => (path ? getRef<T>(path) : null), [path]);

  const [value, isValidating, error, snapshot] = useDocumentData<T>(path ? ref : undefined);

  const data = useMemo<DbDocument<T> | undefined>(
    () => (value && snapshot ? { ...value, id: snapshot.id } : undefined),
    [snapshot, value]
  );

  return {
    data,
    isValidating,
    error,
  };
}

export function useDocumentOnce<T extends {}>(path: string | null) {
  const ref = useMemo(() => (path ? getRef<T>(path) : null), [path]);

  const [value, isValidating, error, snapshot] = useDocumentDataOnce<T>(path ? ref : undefined);

  const data = useMemo<DbDocument<T> | undefined>(
    () => (value && snapshot ? { ...value, id: snapshot.id } : undefined),
    [snapshot, value]
  );

  return {
    data,
    isValidating,
    error,
  };
}

const getCollection = (path: string | null) => {
  if (!path) {
    return null;
  }
  const args = path.split('/');
  const firstArg = args[0] as string;
  return collection(db, firstArg, ...args.slice(1));
};

type Where = [string, WhereFilterOp, string | boolean | number | null];

type CollectionOptions = {
  orderBy?: [string, 'desc' | 'asc'];
  limit?: number;
  where?: Where;
  where2?: Where;
  where3?: Where;
};

export function useCollection<T extends DocumentData>(path: string | null, options?: CollectionOptions) {
  const myCollection = useMemo(() => getCollection(path), [path]);

  const collectionOptions = useMemo(
    () =>
      options
        ? [
            options.where ? where(...options.where) : null,
            options.where2 ? where(...options.where2) : null,
            options.where3 ? where(...options.where3) : null,
            options.orderBy ? orderBy(...options.orderBy) : null,
            options.limit ? limit(options.limit) : null,
          ].filter(isNotNull)
        : [],
    [options]
  );

  const queryValue = useMemo(
    () => (myCollection ? (query(myCollection, ...collectionOptions) as Query<T, DocumentData>) : null),
    [collectionOptions, myCollection]
  );

  const [snaps, isValidating, error] = useCollectionSnaps<T>(queryValue);

  const data = useMemo<DbDocument<T>[]>(
    () => (snaps ? snaps.docs.map((doc) => ({ ...doc.data(), id: doc.id })) : []),
    [snaps]
  );

  return {
    data,
    isValidating,
    error,
  };
}

export function useCollectionOnce<T extends DocumentData>(path: string | null, options?: CollectionOptions) {
  const myCollection = useMemo(() => getCollection(path), [path]);

  const collectionOptions = useMemo(
    () =>
      options
        ? [
            options.where ? where(...options.where) : null,
            options.where2 ? where(...options.where2) : null,
            options.where3 ? where(...options.where3) : null,
            options.orderBy ? orderBy(...options.orderBy) : null,
            options.limit ? limit(options.limit) : null,
          ].filter(isNotNull)
        : [],
    [options]
  );

  const queryValue = useMemo(
    () => (myCollection ? (query(myCollection, ...collectionOptions) as Query<T, DocumentData>) : null),
    [collectionOptions, myCollection]
  );

  const [snaps, isValidating, error] = useCollectionSnapsOnce<T>(queryValue);

  const data = useMemo<DbDocument<T>[]>(
    () => (snaps ? snaps.docs.map((doc) => ({ ...doc.data(), id: doc.id })) : []),
    [snaps]
  );

  return {
    data,
    isValidating,
    error,
  };
}
